import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { DateTime, Duration } from 'luxon';
import PropTypes from 'prop-types';
import React, {
  useState, useRef, useEffect, useTransition,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useRefetchableFragment } from 'react-relay';

import Address from 'src/components/Address';
import LoadingOverlay from 'src/components/Dashboard/LoadingOverlay/index';
import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { carbonLabel } from 'src/enosikit/components/Chart/components/ChartHelpers';
import Heading from 'src/enosikit/components/Heading';
import Map from 'src/enosikit/components/Map';
import FlashesStore from 'src/stores/FlashesStore';
import BrowserProtocol from 'src/util/history';
import {
  INHIBIT_CARBON_DATA_VIEWS, MODAL_CLOSE_DELAY, SOURCE, SOURCE_HISTORIAN,
  SOURCE_TRADES, TIME_ZONE_SYSTEM, UNIT, UNIT_CURRENCY, UNIT_CARBON,
  UNIT_ENERGY, VIEW_SELECTOR, PROPERTY_SHOW_DEFAULT_VIEW,
} from 'src/util/constants';
import { getDefaultLocale } from 'src/util/i18n/handler';

import { dateRangeToTimeRange, timespanChanged } from 'src/util/time';
import { buildMainData } from './data/mainDataBuilder';
import PropertyShowControl from './PropertyShowControl';
import { showTradeRuleMsg, WARNING_TIMEOUT } from './helpers/propertyShow';
import PropertyShowTradeEnergy from './PropertyShowTradeEnergy';
import { timeSpanButtonLabel } from './helpers/common';

const PropertyShowFragment = graphql`
fragment PropertyShow on Query
@refetchable(queryName: "PropertyShowRefetchQuery")
@argumentDefinitions(
  id: {type: "ID!"},
  start: { type: "Timestamp!" },
  finish: { type: "Timestamp!" },
  timeZone: { type: "String!" }
  historianAggregation: { type: "String!" }
  tradeAggregation: { type: "String!"  }
) {
  property(id:$id) {
  id
  externalIdentifier
  active { start finish }
  title
  timezone
  publicHolidayRegion
  address {
    line1
    line2
    city
    state
    country
    postcode
    gps {
      latitude
      longitude
    }
  }
  propertyUsers(first: 500) {
    edges {
      node {
        id
        externalIdentifier
        active { start finish }
        user {
          id
          externalIdentifier
          email
          givenName
          familyName
          active { start finish }
        }
      }
    }
  }
  meters(first:500) {
    edges {
      node {
        id
        identifier
        externalIdentifier
        active { start finish }
        title
        tradePointId
        property {
          title
        }
        primaryBillingPoint {
          identifier
        }
        rules(first:999999999, start: $start, finish: $finish) {
          edges {
            node {
              id
              tradeType
              buyer {
                userId
                user {
                  id
                  email
                  givenName
                  familyName
                }
                tradePoint {
                  id
                  type
                  meter {
                    id
                    identifier
                    title
                    property {
                      id
                      title
                    }
                  }
                }
              }
              seller {
                userId
                user {
                  id
                  email
                  givenName
                  familyName
                }
                tradePoint {
                  id
                  type
                  meter {
                    id
                    identifier
                    title
                    property {
                      id
                      title
                    }
                  }
                }
              }
              state
              clauses {
                edges {
                  node {
                    price
                    ignorePublicHolidays
                    monthsOfYear
                    daysOfWeek
                    timesOfDay {
                      start { hours minutes }
                      finish { hours minutes }
                    }
                  }
                }
              }
              proposedAt
              proposedBy {
                id
                email
                givenName
                familyName
              }
              acceptedAt
              acceptedBy {
                id
                email
                givenName
                familyName
              }
              start
              finish
            }
          }
        }
        dataConsumed: calculatedData(start: $start, finish: $finish, aggregation: $historianAggregation, timeZone: $timeZone, metric: "elec_energy_consumed") {
          aggregation
          metric {
            identifier
          }
          timeZone
          timeRange {
            start
            finish
          }
          data {
            timestamp
            value
            flags {
              identifier
              description
            }
          }
        }
        dataGenerated: calculatedData(start: $start, finish: $finish, aggregation: $historianAggregation, timeZone: $timeZone, metric: "elec_energy_generated") {
          aggregation
          metric {
            identifier
          }
          timeZone
          timeRange {
            start
            finish
          }
          data {
            timestamp
            value
            flags {
              identifier
              description
            }
          }
        }
        tradeSetSummary(start: $start, finish: $finish, timeZone: $timeZone, aggregation: $tradeAggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
          key {
            tradePointId
            ruleId
            direction
            type
          }
          data {
            range { start finish }
            buyerTradePointIds
            sellerTradePointIds
            directions
            types
            value
            volume
            averagePrice
            tradeCount
            intervalCount
          }
        }
      }
    }
  }
 }
}
`;

/**
 * Provides the input for the chart display (carbon/currency/wh) type selector
 * @param {string} unit
 * @param {string} source
 * @returns {Array<object>} - chart display control options.
 */
const controlOptionFunc = (unit, source) => {
  const inhibitCarbonDataViews = APIConfig().feature(INHIBIT_CARBON_DATA_VIEWS);
  const unitOps = {
    group: UNIT,
    items: [{
      value: UNIT_ENERGY, label: 'Wh', active: (unit === UNIT_ENERGY), disabled: false,
    }, {
      value: UNIT_CURRENCY, label: <FormattedMessage id="property.property_show.type_selector.currency.label" defaultMessage="$" />, active: (unit === UNIT_CURRENCY), disabled: (source === SOURCE_HISTORIAN),
    }],
  };
  if (inhibitCarbonDataViews) {
    return [unitOps];
  }

  const carbonController = {
    value: UNIT_CARBON, label: carbonLabel(), active: (unit === UNIT_CARBON),
  };
  unitOps.items.push(carbonController);
  return [unitOps];
};

/**
 * @param {import('react-intl').IntlShape} intl - i18n
 * @returns {React.ReactComponentElement} - warning dialog fragment
 */
const warningDialog = (intl) => {
  const msgWarning = intl.formatMessage({
    id: 'property.property_show.property_show_helpers.message_warning',
    defaultMessage: 'Hi! Are you trying to view your trade rules? Create and manage them by selecting Trade Rules in the menu above.',
  });
  return (
    <>
      <FontAwesomeIcon className="me-1" size="lg" icon={faTriangleExclamation} />
      {msgWarning}
    </>
  );
};

const PROGRESS_INITIALISED = 33;
const PROGRESS_PREPARING_DATA = 67;
const PROGRESS_FULLY_LOADED = 100;

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - PropertyShow component
 */
function PropertyShow(props) {
  if (!props) {
    return <Loading />;
  }

  const { timezone } = props;
  const [data, refetch] = useRefetchableFragment(PropertyShowFragment, props);
  const { property } = data || {};
  const { __fragmentOwner } = props || {};
  const { variables } = __fragmentOwner || {};
  const { start, finish } = variables || {};
  const propertyTimezone = property.timezone || APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
  const tz = timezone || propertyTimezone;

  let s = DateTime.local().startOf('day').minus(Duration.fromISO('P1M1D'));
  if (start && start !== undefined) {
    const ts = DateTime.fromSeconds(start);
    if (ts.invalidExplanation === null) {
      s = ts;
    }
  }

  let f = s.plus(Duration.fromISO('P1M'));
  if (finish && finish !== undefined) {
    const ts = DateTime.fromSeconds(finish);
    if (ts.invalidExplanation === null) {
      f = ts.minus(Duration.fromISO('P1D'));
    }
  }

  const duration = f.diff(s);

  let agg = 'PT30M';
  if (duration.as('days') > 7) {
    agg = 'P1D';
  }

  // I18n messages for loading overlay
  const intl = useIntl();
  const loadingData = intl.formatMessage({ id: 'property.property_show.loading_data.overlay.body.message', defaultMessage: 'Loading data...' });
  const preparingDataAndRendering = intl.formatMessage({ id: 'property.property_show.preparing_data_and_rendering.overlay.body.message', defaultMessage: 'Preparing data and rendering.' });
  const newDateRangeApplied = intl.formatMessage({ id: 'property.property_show.new_date_range_applied.overlay.body.message', defaultMessage: 'New date range applied.' });

  const [historianAggregation, setHistorianAggregation] = useState(agg);
  const [tradeAggregation, setTradeAggregation] = useState(agg);
  const [datespan, setDatespan] = useState({
    start: s,
    finish: f,
  });
  const [isPending, startTransition] = useTransition();
  const [unit, setUnit] = useState(UNIT_ENERGY);
  const [source, setSource] = useState(SOURCE_TRADES);
  const [tradeBtnClicks, setTradeBtnClicks] = useState(0);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [downloadSelectorOpen, setDownloadSelectorOpen] = useState(false);
  const [chartView, setChartView] = useState(PROPERTY_SHOW_DEFAULT_VIEW);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [progressBarLabel, setProgressBarLabel] = useState('');
  const firstTimeRender = useRef(true);

  const mainData = buildMainData(property, datespan);

  const closeAndResetModal = () => {
    setProgressBarLabel('');
    setProgressBarValue(0);
    setIsModalOpen(false);
  };

  const doRefetch = () => {
    const { start: startTime, finish: finishTime } = dateRangeToTimeRange(
      datespan,
      timezone,
    );
    const timespanDuration = finishTime.diff(startTime);

    let aggregate = 'PT30M';
    if (timespanDuration.as('days') > 7) {
      aggregate = 'P1D';
    }

    // Step 1: modal open → 33% + "Loading data..."
    setIsModalOpen(true);
    setProgressBarValue(PROGRESS_INITIALISED);
    setProgressBarLabel(loadingData);

    startTransition(() => {
      refetch(
        {
          start: startTime.toSeconds(),
          finish: finishTime.toSeconds(),
          timeZone: tz,
          historianAggregation: aggregate,
          tradeAggregation: aggregate,
        },
        {
          onComplete: (error) => {
            if (error) {
              FlashesStore.flash(FlashesStore.ERROR, error);
              closeAndResetModal();
              return;
            }

            // Step 2: data loaded → 67% + "Preparing data and rendering"
            setProgressBarValue(PROGRESS_PREPARING_DATA);
            setProgressBarLabel(preparingDataAndRendering);

            // Step 3: ready to render → 100% + "Loading complete"
            requestAnimationFrame(() => {
              setProgressBarValue(PROGRESS_FULLY_LOADED);
              setProgressBarLabel(newDateRangeApplied);

              // Step 4: reset after short delay
              setTimeout(() => {
                closeAndResetModal();
              }, MODAL_CLOSE_DELAY);
            });

            const { match } = props;
            const { location } = match;
            const { pathname } = location;
            setHistorianAggregation(aggregate);
            setTradeAggregation(aggregate);

            const state = {
              start: startTime.toISODate({ zone: tz }),
              finish: finishTime.minus({ days: 1 }).toISODate({ zone: tz }),
            };

            BrowserProtocol.navigate({
              action: 'REPLACE',
              pathname,
              search: `?${Object.keys(state).map((k) => [k, encodeURIComponent(state[k])].join('=')).join('&')}`,
              hash: '',
              state,
            });
          },
        },
      );
    });
  };

  useEffect(() => {
    if (!firstTimeRender.current) {
      doRefetch();
    }
  }, [datespan]);

  useEffect(() => {
    firstTimeRender.current = false;
  }, []);

  const toggle = (dropdownType) => {
    if (dropdownType === VIEW_SELECTOR) {
      setViewSelectorOpen(!viewSelectorOpen);
    } else {
      setDownloadSelectorOpen(!downloadSelectorOpen);
    }
  };

  const controlSetStateFunc = (opts) => {
    const { group, value } = opts;

    let chartSource = source;
    let chartUnit = unit;
    let btnClicks = tradeBtnClicks;
    const warningMessage = warningDialog(intl);

    FlashesStore.reset();
    if (value === chartSource && chartSource === SOURCE_TRADES) {
      if (btnClicks === 0) {
        clearTimeout();
        btnClicks += 1;
      } else {
        showTradeRuleMsg(warningMessage);
      }
      setTimeout(() => {
        setTradeBtnClicks(0);
      }, WARNING_TIMEOUT);
    }
    if (group === UNIT) {
      chartUnit = value;
    }
    if (group === SOURCE) {
      chartSource = value;
      if (chartUnit === UNIT_CURRENCY) {
        chartUnit = UNIT_ENERGY;
      }
    }
    setSource(chartSource);
    setUnit(chartUnit);
    setTradeBtnClicks(btnClicks);
  };

  const handleTimespanUpdate = (updatedStart, updatedFinish) => {
    const inputStart = updatedStart?.setZone(tz)?.startOf('day');
    const inputFinish = updatedFinish?.setZone(tz)?.startOf('day');
    const currStart = datespan.start?.setZone(tz)?.startOf('day');
    const currFinish = datespan.finish?.setZone(tz)?.startOf('day');
    const currentDatespan = { start: currStart, finish: currFinish };
    const isTimespanChanged = timespanChanged(inputStart, inputFinish, currentDatespan);
    if (!isTimespanChanged) {
      return;
    }
    setDatespan({ start: updatedStart, finish: updatedFinish });
    setProgressBarLabel(loadingData);
  };

  const renderPropertyControls = (propertyShowData) => {
    const { start: startTime, finish: finishTime } = datespan;
    const locale = getDefaultLocale();
    const updatedStartTime = startTime.setLocale(locale).setZone(tz);
    const updatedFinishTime = finishTime.setLocale(locale).setZone(tz);

    return (
      <PropertyShowControl
        mainData={propertyShowData}
        timespan={{ start: updatedStartTime, finish: updatedFinishTime }}
        timespanUpdateFunc={handleTimespanUpdate}
        timezone={tz}
        controlOptionFunc={() => controlOptionFunc(unit, source)}
        controlSetStateFunc={(opts) => controlSetStateFunc(opts)}
        toggle={toggle}
        viewSelectorOpen={viewSelectorOpen}
        downloadSelectorOpen={downloadSelectorOpen}
        isModalOpen={isModalOpen}
        setChartView={setChartView}
        chartView={chartView}
        source={source}
      />
    );
  };

  const {
    address, title,
  } = property;
  const addr = new Address(address);

  if (!mainData) {
    const pageError = intl.formatMessage({ id: 'property.property_show.page.error', defaultMessage: 'Data unable to be processed.' });
    FlashesStore.flash(FlashesStore.ERROR, pageError);
    return null;
  }

  const pageTitle = intl.formatMessage({ id: 'property.property_show.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' }, { propertyTitle: title });

  // Loading overlay props
  const loadingOverlayProps = {
    bodyLabel: intl.formatMessage({
      id: 'property.property_show.applying_new_date_range.overlay.body.message',
      defaultMessage: 'Updating to show {dateRange}.',
    }, { dateRange: timeSpanButtonLabel(datespan.start, datespan.finish, timezone) }),
    headerLabel: (
      <FormattedMessage
        id="property.property_show.applying_new_date_range.overlay.header.message"
        defaultMessage="{calenderIcon} Applying new date range"
        values={{
          calenderIcon: <FontAwesomeIcon icon={faCalendar} />,
        }}
      />
    ),
    progressBarValue,
    progressLabel: progressBarLabel,
    updateProgress: setIsModalOpen,
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      {(isPending || isModalOpen) && <LoadingOverlay inputs={loadingOverlayProps} />}
      <Breadcrumbs breadcrumbs={[{
        name: <FormattedMessage id="property.property_show.breadcrumbs.properties.label" defaultMessage=" Properties" />, path: '/properties',
      }, { name: title }]}
      />
      <Heading
        alias={title}
        address={addr.simpleString()}
        context={<FormattedMessage id="property.property_show.heading.overview.title" defaultMessage="Overview" />}
        controls={renderPropertyControls(mainData)}
        subtitle={addr.simpleString()}
        title={title}
      />
      <PropertyShowTradeEnergy
        mainData={mainData}
        historianAggregation={historianAggregation}
        tradeAggregation={tradeAggregation}
        timespan={dateRangeToTimeRange(
          datespan,
          tz,
        )}
        handleTimespanUpdate={handleTimespanUpdate}
        unit={unit}
        source={source}
        controlSetStateFunc={(opts) => controlSetStateFunc(opts)}
        chartView={chartView}
        timezone={tz}
      />
      {address && <Map address={address} />}
    </>
  );
}

export default PropertyShow;

PropertyShow.propTypes = {
  match: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
  relay: PropTypes.shape({
    refetch: PropTypes.func,
  }),
  timezone: PropTypes.string,
};

PropertyShow.defaultProps = {
  relay: undefined,
  timezone: undefined,
};
